import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Building2, Eye, EyeOff, Mail, ArrowLeft, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatApiErrorDetail(detail) {
  if (detail == null) return null;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).filter(Boolean).join(' ');
  if (detail?.msg) return detail.msg;
  return String(detail);
}

function ForgotPasswordForm({ onBack }) {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || 'Грешка при изпращане');
    } finally {
      setLoading(false);
    }
  };

  if (sent) return (
    <div className="text-center space-y-4">
      <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
        <CheckCircle className="w-8 h-8 text-green-600" />
      </div>
      <h2 className="font-heading font-bold text-xl">Заявката е изпратена</h2>
      <p className="text-muted-foreground text-sm">
        Линк за смяна на паролата е изпратен до администратора. Моля изчакайте.
      </p>
      <Button variant="outline" className="w-full" onClick={onBack}>
        <ArrowLeft className="w-4 h-4 mr-2" />Обратно към вход
      </Button>
    </div>
  );

  return (
    <div className="space-y-5">
      <div className="text-center">
        <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-3">
          <Mail className="w-6 h-6 text-primary" />
        </div>
        <h2 className="font-heading font-bold text-xl">Забравена парола</h2>
        <p className="text-muted-foreground text-sm mt-1">
          Въведете имейла си и администраторът ще получи заявка за смяна на паролата.
        </p>
      </div>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <Label htmlFor="reset-email">Имейл</Label>
          <Input
            id="reset-email"
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder="вашия@имейл.com"
            required
            className="mt-1"
          />
        </div>
        <Button type="submit" className="w-full" disabled={loading}>
          {loading ? <><div className="spinner w-4 h-4 mr-2" />Изпращане...</> : 'Изпрати заявка'}
        </Button>
        <Button type="button" variant="ghost" className="w-full" onClick={onBack}>
          <ArrowLeft className="w-4 h-4 mr-2" />Обратно към вход
        </Button>
      </form>
    </div>
  );
}

export default function LoginPage() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showForgot, setShowForgot] = useState(false);
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  if (isAuthenticated) return <Navigate to="/admin" replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
      toast.success(t('login.success'));
      navigate('/admin');
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || t('login.error');
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-secondary flex items-center justify-center p-4" data-testid="login-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <a href="/" className="inline-flex items-center gap-3">
            <div className="w-12 h-12 bg-primary rounded-xl flex items-center justify-center">
              <Building2 className="w-7 h-7 text-white" />
            </div>
            <span className="font-heading font-bold text-2xl text-primary">GreenCastle</span>
          </a>
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-8 border border-border">
          {showForgot ? (
            <ForgotPasswordForm onBack={() => setShowForgot(false)} />
          ) : (
            <>
              <h1 className="font-heading text-2xl font-bold text-foreground mb-2 text-center">{t('login.title')}</h1>
              <p className="text-muted-foreground text-center mb-8">{t('login.subtitle')}</p>
              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <Label htmlFor="email">{t('login.email')}</Label>
                  <Input id="email" type="email" value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="admin@greencastle.bg" required
                    autoComplete="email" data-testid="login-email-input" />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <Label htmlFor="password">{t('login.password')}</Label>
                    <button type="button" onClick={() => setShowForgot(true)}
                      className="text-xs text-primary hover:underline">
                      Забравена парола?
                    </button>
                  </div>
                  <div className="relative">
                    <Input id="password" type={showPassword ? 'text' : 'password'}
                      value={password} onChange={e => setPassword(e.target.value)}
                      placeholder="••••••••" required autoComplete="current-password"
                      className="pr-10" data-testid="login-password-input" />
                    <button type="button" onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                {error && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm" data-testid="login-error">
                    {error}
                  </div>
                )}
                <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit-btn">
                  {loading ? <><div className="spinner w-4 h-4 mr-2" />{t('login.loading')}</> : t('login.submit')}
                </Button>
              </form>
            </>
          )}
        </div>

        <p className="text-center text-sm text-muted-foreground mt-6">
          <a href="/" className="hover:text-primary transition-colors">&larr; {t('login.back')}</a>
        </p>
      </div>
    </div>
  );
}
